import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  User, Mail, LogOut, AlertTriangle, Calendar, CalendarClock, CalendarCheck,
  Clock, ListChecks, ShieldOff, ClipboardList, Laptop, FileText, MessageSquare,
} from 'lucide-react';
import api from '../../../services/api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 0 32px;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 20px 22px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  color: ${({ theme }) => theme.colors.text.dark.little};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-bottom: 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px 24px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
  display: flex;
  gap: 10px;
  font-size: 14px;
  padding: 8px 0;
`;

const FieldIcon = styled.div`
  color: ${({ theme }) => theme.colors.primary.main};
  flex-shrink: 0;
  margin-top: 2px;
`;

const FieldLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.colors.text.dark.little};
  margin-bottom: 2px;
`;

const FieldValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-word;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ $tone }) => $tone === 'green' ? '#E8F8F0'
    : $tone === 'red' ? '#FEE6E6'
    : $tone === 'amber' ? '#FFF5E1'
    : '#E8F4FD'};
  color: ${({ $tone }) => $tone === 'green' ? '#1A6E38'
    : $tone === 'red' ? '#A02525'
    : $tone === 'amber' ? '#8A5500'
    : '#024F87'};
`;

const Stepper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const Step = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: ${({ $state }) => ($state === 'current' ? 700 : 500)};
  background: ${({ $state }) => $state === 'done' ? '#E8F8F0'
    : $state === 'current' ? '#0291EF'
    : '#EEF1F4'};
  color: ${({ $state }) => $state === 'done' ? '#1A6E38'
    : $state === 'current' ? '#fff'
    : '#8A95A5'};
`;

const Arrow = styled.span`
  color: #C3CCD6;
  font-size: 12px;
`;

const ProgressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const ProgressCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #E8ECF0;
  border-radius: 10px;
  padding: 14px 16px;
`;

const ProgressIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: #EEF6FF;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary.main};
  flex-shrink: 0;
`;

const ProgressLabel = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

const ProgressValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Main status path (cancelled is an off-path terminal state, shown separately).
const FLOW = [
  'initiated', 'in_progress', 'pending_handover_approval',
  'pending_it_zero_access', 'pending_closure', 'completed',
];

const STATUS_TONE = {
  initiated: 'blue', in_progress: 'blue',
  pending_handover_approval: 'amber', pending_it_zero_access: 'amber', pending_closure: 'amber',
  completed: 'green', cancelled: 'red',
};

const fmtDate = (d, lang) => {
  if (!d) return '–';
  try { return new Date(d).toLocaleDateString(lang || 'tr'); } catch { return '–'; }
};

export default function OffboardingOverviewTab({ offboarding }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [lists, setLists] = useState(null);

  const id = offboarding?.id;

  useEffect(() => {
    if (!id) return undefined;
    let active = true;
    const eps = {
      tasks: '/v1/offboarding-tasks',
      itAccess: '/v1/offboarding-it-access',
      handover: '/v1/offboarding-handovers',
      equipment: '/v1/offboarding-equipment',
      legalDocs: '/v1/offboarding-legal-docs',
      exitInterview: '/v1/offboarding-exit-interviews',
    };
    Promise.all(Object.entries(eps).map(([key, url]) =>
      api.get(`${url}?offboardingId[eq]=${id}&pageSize=200&pageNumber=1`)
        .then((res) => {
          const r = res.data?.data;
          return [key, Array.isArray(r) ? r : (r?.data || [])];
        })
        .catch(() => [key, []])
    )).then((pairs) => { if (active) setLists(Object.fromEntries(pairs)); });
    return () => { active = false; };
  }, [id]);

  const progress = useMemo(() => {
    if (!lists) return null;
    const cnt = (arr, pred) => `${(arr || []).filter(pred).length} / ${(arr || []).length}`;
    const firstStatus = (arr) => (arr && arr.length ? arr[0].status : null);
    return {
      tasks: cnt(lists.tasks, (x) => x.status === 'completed'),
      itAccess: cnt(lists.itAccess, (x) => x.isVerified || x.status === 'verified'),
      equipment: cnt(lists.equipment, (x) => x.isConfirmed || x.status === 'confirmed'),
      legalDocs: cnt(lists.legalDocs, (x) => x.status === 'signed' || x.status === 'stored'),
      handover: firstStatus(lists.handover),
      exitInterview: firstStatus(lists.exitInterview),
    };
  }, [lists]);

  if (!offboarding) return null;

  const tone = STATUS_TONE[offboarding.status] || 'blue';
  const currentIdx = FLOW.indexOf(offboarding.status);
  const isCancelled = offboarding.status === 'cancelled';

  return (
    <Container>
      {/* Summary card */}
      <Card>
        <CardHeader><LogOut size={14} />{t('offboarding.detail.summary')}</CardHeader>
        <Grid>
          <Field>
            <FieldIcon><User size={16} /></FieldIcon>
            <div><FieldLabel>{t('offboarding.person')}</FieldLabel><FieldValue>{offboarding.personFullName || '–'}</FieldValue></div>
          </Field>
          <Field>
            <FieldIcon><Mail size={16} /></FieldIcon>
            <div><FieldLabel>{t('offboarding.email')}</FieldLabel><FieldValue>{offboarding.personEmail || '–'}</FieldValue></div>
          </Field>
          <Field>
            <FieldIcon><LogOut size={16} /></FieldIcon>
            <div>
              <FieldLabel>{t('offboarding.exitTypeLabel')}</FieldLabel>
              <FieldValue>{t(`offboarding.exitType.${offboarding.exitType}`, { defaultValue: offboarding.exitType })}</FieldValue>
            </div>
          </Field>
          <Field>
            <FieldIcon><AlertTriangle size={16} /></FieldIcon>
            <div>
              <FieldLabel>{t('offboarding.urgencyLabel')}</FieldLabel>
              <FieldValue>{t(`offboarding.urgency.${offboarding.urgency}`, { defaultValue: offboarding.urgency })}</FieldValue>
            </div>
          </Field>
          <Field>
            <FieldIcon><Clock size={16} /></FieldIcon>
            <div>
              <FieldLabel>{t('offboarding.statusLabel')}</FieldLabel>
              <FieldValue>
                <Badge $tone={tone}>{t(`offboarding.status.${offboarding.status}`, { defaultValue: offboarding.status })}</Badge>
              </FieldValue>
            </div>
          </Field>
          <Field>
            <FieldIcon><Calendar size={16} /></FieldIcon>
            <div><FieldLabel>{t('offboarding.initiatedAt')}</FieldLabel><FieldValue>{fmtDate(offboarding.initiatedAt, lang)}</FieldValue></div>
          </Field>
          <Field>
            <FieldIcon><CalendarClock size={16} /></FieldIcon>
            <div><FieldLabel>{t('offboarding.exitDate')}</FieldLabel><FieldValue>{fmtDate(offboarding.exitDate, lang)}</FieldValue></div>
          </Field>
          <Field>
            <FieldIcon><CalendarCheck size={16} /></FieldIcon>
            <div><FieldLabel>{t('offboarding.lastWorkDay')}</FieldLabel><FieldValue>{fmtDate(offboarding.lastWorkDay, lang)}</FieldValue></div>
          </Field>
          <Field>
            <FieldIcon><Clock size={16} /></FieldIcon>
            <div>
              <FieldLabel>{t('offboarding.form.noticePeriodDays')}</FieldLabel>
              <FieldValue>{offboarding.noticePeriodDays != null ? offboarding.noticePeriodDays : '–'}</FieldValue>
            </div>
          </Field>
        </Grid>
      </Card>

      {/* Status stepper */}
      <Card>
        <CardHeader><ListChecks size={14} />{t('offboarding.detail.progress')}</CardHeader>
        {isCancelled ? (
          <Badge $tone="red">{t('offboarding.status.cancelled')}</Badge>
        ) : (
          <Stepper>
            {FLOW.map((s, i) => {
              const state = i < currentIdx ? 'done' : i === currentIdx ? 'current' : 'todo';
              return (
                <React.Fragment key={s}>
                  {i > 0 && <Arrow>›</Arrow>}
                  <Step $state={state}>{t(`offboarding.status.${s}`, { defaultValue: s })}</Step>
                </React.Fragment>
              );
            })}
          </Stepper>
        )}
      </Card>

      {/* Sub-process progress */}
      <Card>
        <CardHeader><ClipboardList size={14} />{t('offboarding.detail.subProcesses')}</CardHeader>
        <ProgressGrid>
          <ProgressCard>
            <ProgressIcon><ListChecks size={18} /></ProgressIcon>
            <div>
              <ProgressLabel>{t('offboarding.detail.tab.tasks')}</ProgressLabel>
              <ProgressValue>{progress ? progress.tasks : '…'}</ProgressValue>
            </div>
          </ProgressCard>
          <ProgressCard>
            <ProgressIcon><ShieldOff size={18} /></ProgressIcon>
            <div>
              <ProgressLabel>{t('offboarding.detail.tab.itAccess')}</ProgressLabel>
              <ProgressValue>{progress ? progress.itAccess : '…'}</ProgressValue>
            </div>
          </ProgressCard>
          <ProgressCard>
            <ProgressIcon><ClipboardList size={18} /></ProgressIcon>
            <div>
              <ProgressLabel>{t('offboarding.detail.tab.handover')}</ProgressLabel>
              <ProgressValue>
                {progress
                  ? (progress.handover
                    ? t(`offboardingHandover.status.${progress.handover}`, { defaultValue: progress.handover })
                    : t('offboarding.detail.noData'))
                  : '…'}
              </ProgressValue>
            </div>
          </ProgressCard>
          <ProgressCard>
            <ProgressIcon><Laptop size={18} /></ProgressIcon>
            <div>
              <ProgressLabel>{t('offboarding.detail.tab.equipment')}</ProgressLabel>
              <ProgressValue>{progress ? progress.equipment : '…'}</ProgressValue>
            </div>
          </ProgressCard>
          <ProgressCard>
            <ProgressIcon><FileText size={18} /></ProgressIcon>
            <div>
              <ProgressLabel>{t('offboarding.detail.tab.legalDocs')}</ProgressLabel>
              <ProgressValue>{progress ? progress.legalDocs : '…'}</ProgressValue>
            </div>
          </ProgressCard>
          <ProgressCard>
            <ProgressIcon><MessageSquare size={18} /></ProgressIcon>
            <div>
              <ProgressLabel>{t('offboarding.detail.tab.exitInterview')}</ProgressLabel>
              <ProgressValue>
                {progress
                  ? (progress.exitInterview
                    ? t(`offboardingExitInterview.status.${progress.exitInterview}`, { defaultValue: progress.exitInterview })
                    : t('offboarding.detail.noData'))
                  : '…'}
              </ProgressValue>
            </div>
          </ProgressCard>
        </ProgressGrid>
      </Card>
    </Container>
  );
}
